import React, { FC, Fragment } from 'react';
import moment from 'moment';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { closeModal } from '@src/redux/actions/modal';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { useAppDispatch } from '@src/redux';
import { usePublishOpeningDataQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { DetailsList, FooterInfoBlock, ModalInfo, ModalActions, ModalContainer, ModalTitle } from '../components';
import { PublishOpeningModalProps } from './props';

export const PublishOpeningModal: FC<PublishOpeningModalProps> = ({ nextFunction }) => {
  const dispatch = useAppDispatch();
  const {
    loading: isQueryLoading,
    error,
    data,
  } = usePublishOpeningDataQuery({
    fetchPolicy: 'network-only',
    onError: () => setErrorFlashMessage(),
  });

  if (isQueryLoading) {
    return <ModalContainer isLoading />;
  }

  if (error) {
    closeModal();

    return null;
  }

  const {
    customer: {
      id: customerId,
      planInfo: {
        currency: { symbol },
        openingActivationPrice,
        nextPaymentDate,
      },
    },
  } = data;

  return (
    <ModalContainer>
      <ModalTitle>Confirm and publish</ModalTitle>
      <ModalInfo>Your subscription includes one active opening. Purchase an extra opening to continue.</ModalInfo>
      <DetailsList
        details={[
          {
            name: 'Price per extra opening',
            value: (
              <Fragment>
                <strong>
                  +{symbol}
                  {openingActivationPrice}
                </strong>
                /mo
              </Fragment>
            ),
          },
          {
            name: 'Renewal date*',
            value: moment(nextPaymentDate).format('DD MMM YYYY'),
          },
        ]}
      />
      <ModalActions
        actions={[
          {
            name: 'Confirm',
            onClick: () => {
              trackEvent({
                context: MetricContext.BILLING,
                interaction: MetricAction.ACTIVATE_OPENING,
                payload: {
                  'Customer': customerId,
                  'Opening Activation Price': openingActivationPrice,
                },
              });

              dispatch(closeModal());
              nextFunction();
            },
          },
          {
            buttonAppearance: 'secondary',
            name: 'Close',
            onClick: () => dispatch(closeModal()),
          },
        ]}
      />
      <FooterInfoBlock text="Note: *Extra openings are billed in a 30-day cycle starting from the publishing date." />
    </ModalContainer>
  );
};
